import { useSessionStore } from "@/stores/session.store";
import type { TerritoriesAIApiV1CommonResponse } from "@territoriesai/types";
import axios, { AxiosError, type AxiosRequestConfig } from "axios";
import _ from "lodash";
import { useRouter } from "vue-router";

export type HttpMethods = "get" | "post" | "put" | "delete";

export interface TaiRequestConfig<RequestBody, Params>
  extends AxiosRequestConfig<RequestBody> {
  params?: Params;
  method?: HttpMethods;
}

export const useHttpClient = <RequestBody, Params>(
  options: TaiRequestConfig<RequestBody, Params> = {
    method: "get",
    params: {} as Params,
    data: {} as RequestBody
  },
  settings?: {
    baseUrl?: string;
    // determines whether to add the idToken to the request Authorization header. Dedaults to true
    secured?: boolean;
  }
) => {
  const baseURL = settings?.baseUrl || import.meta.env.VITE_TAI_API_BASE_URL;
  // only ignore secured if explicitly set to false
  const secured = settings?.secured !== false;
  const axiosInstance = axios.create({
    baseURL
  });
  const router = useRouter();
  const { selectedSession, signOut } = useSessionStore();
  const session = selectedSession();
  const config: AxiosRequestConfig<RequestBody> = {
    ...options,
    headers: {
      ...options.headers,
      ...(() =>
        secured
          ? {
              Authorization: session?.idToken
            }
          : {})()
    }
  };

  axiosInstance.interceptors.response.use(
    (res) => res,
    (error) => {
      if ([401].includes(error.response.status)) {
        signOut();
        router.push({ name: "SignIn" });
      }
      return error;
    }
  );

  return {
    request: async <ResponseData extends TerritoriesAIApiV1CommonResponse>(
      // allows us to pass params and request body on request call instead of during composable call
      metadata: {
        url?: string;
        params?: Params;
        data?: RequestBody;
        headers?: Record<string, string>;
      } = {},

      // allows us to pass mock data on request call instead of during composable call
      mock?: { mockData: () => ResponseData; timeout?: number }
    ): Promise<ResponseData> => {
      if (import.meta.env.VITE_TAI_ENABLE_API_MOCK === "true" && mock)
        return new Promise((resolve) => {
          setTimeout(() => {
            resolve(mock.mockData());
          }, mock.timeout || 1000);
        }) as Promise<ResponseData>;

      const response = await axiosInstance<ResponseData>(
        _.merge(metadata, config)
      );

      if (response instanceof AxiosError) {
        const error = response as AxiosError<TerritoriesAIApiV1CommonResponse>;

        throw new Error(
          (error.response?.data.errorMessage as string) || error.message
        );
      }

      return response.data;
    }
  };
};
